import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserRound, ArrowLeft, Save, Lock, Mail, Bell } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { NotificationPrefs } from "@/components/NotificationPrefs";
import { PasswordSection } from "@/components/PasswordSection";
import { TrustedDevices } from "@/components/TrustedDevices";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/usePageMeta";

const fld = "w-full rounded-xl border border-sbg-raspberry/20 bg-white px-4 py-2.5 text-sm text-sbg-burgundy outline-none focus:border-sbg-fuchsia";

export default function Account() {
  usePageMeta({ title: "My account | Southern Black Girls Toolkits", description: "Staff account settings." });
  const { user, loading: authLoading, login } = useAuth();
  const [acct, setAcct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (user?.is_staff) api.get("/me/account").then((r) => setAcct(r.data)).catch(() => toast.error("Could not load your account."));
  }, [user?.is_staff, user?.email]);

  if (authLoading) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" /></div>;

  if (!user?.is_staff)
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <SiteNav />
        <div className="mx-auto max-w-md px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-black text-sbg-burgundy">Staff only</h1>
          <p className="mt-3 text-sbg-burgundy/70">Sign in with an admin or manager account to manage your settings.</p>
          <div className="mt-6"><Link to="/admin/login" className="rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white">Sign in</Link></div>
        </div>
        <SiteFooter />
      </div>
    );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/account", { name: acct.name, notif_prefs: acct.notif_prefs });
      setAcct(data); setDirty(false);
      toast.success("Settings saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-3xl px-5 py-10">
        <Link to="/admin" data-testid="account-back-link" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sbg-raspberry hover:underline"><ArrowLeft className="h-4 w-4" /> Back to admin</Link>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sbg-burgundy text-white"><UserRound className="h-6 w-6" /></span>
          <div>
            <h1 className="font-display text-3xl font-black text-sbg-burgundy">My account</h1>
            <p className="text-sm text-sbg-burgundy/60">Your profile and email notification preferences</p>
          </div>
        </div>

        {!acct ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-sbg-raspberry" /></div> : (
          <div className="mt-8 space-y-8">
            <section className="rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10" data-testid="account-profile">
              <div className="text-xs font-bold uppercase tracking-wider text-sbg-raspberry">Profile</div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-bold text-sbg-burgundy">Name</span>
                  <input data-testid="account-name" className={`${fld} mt-1`} value={acct.name} placeholder="Your name"
                    onChange={(e) => { setAcct((a) => ({ ...a, name: e.target.value })); setDirty(true); }} />
                </label>
                <div>
                  <span className="text-sm font-bold text-sbg-burgundy">Role</span>
                  <div className="mt-1 inline-flex items-center gap-2 rounded-xl bg-sbg-blush/50 px-4 py-2.5 text-sm font-semibold capitalize text-sbg-burgundy" data-testid="account-role">{acct.role}</div>
                </div>
              </div>
              <EmailChanger acct={acct} onChanged={(data) => { login(data.token, data); setAcct((a) => ({ ...a, email: data.email })); }} />
            </section>

            <PasswordSection />

            <TrustedDevices />

            <section className="rounded-3xl bg-white/60 p-6 ring-1 ring-sbg-raspberry/10">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sbg-raspberry"><Bell className="h-4 w-4" /> Email notifications</div>
              <p className="mt-2 text-sm text-sbg-burgundy/70">Choose how you hear about each event: <strong>Instant</strong> sends an email right away, <strong>Daily summary</strong> bundles everything into one email at 5:00 AM Eastern, <strong>Off</strong> stays quiet.</p>
              <div className="mt-5">
                <NotificationPrefs events={acct.events} prefs={acct.notif_prefs}
                  onChange={(k, v) => { setAcct((a) => ({ ...a, notif_prefs: { ...a.notif_prefs, [k]: v } })); setDirty(true); }} />
              </div>
            </section>

            <div className="sticky bottom-4 flex justify-end">
              <button data-testid="account-save-btn" onClick={save} disabled={!dirty || saving}
                className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-6 py-3 text-sm font-bold text-white shadow-lg disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
              </button>
            </div>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}

function EmailChanger({ acct, onChanged }) {
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("idle");
  const [busy, setBusy] = useState(false);

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/email/request", { new_email: newEmail.trim() });
      setStage("code"); toast.success(`Code sent to ${data.to}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send code."); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/email/confirm", { new_email: newEmail.trim(), code });
      onChanged(data); setStage("idle"); setNewEmail(""); setCode("");
      toast.success("Email updated.");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code."); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-5 border-t border-sbg-raspberry/10 pt-5">
      <span className="text-sm font-bold text-sbg-burgundy">Email address</span>
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <span data-testid="account-email" className="inline-flex items-center gap-2 rounded-xl bg-sbg-blush/50 px-4 py-2.5 text-sm font-semibold text-sbg-burgundy"><Mail className="h-4 w-4" /> {acct.email}</span>
        {acct.env ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-sbg-burgundy/60" data-testid="account-email-locked"><Lock className="h-3.5 w-3.5" /> Owner account — email is fixed</span>
        ) : stage === "idle" ? (
          <button data-testid="account-change-email-btn" onClick={() => setStage("edit")} className="text-sm font-bold text-sbg-fuchsia hover:underline">Change email</button>
        ) : null}
      </div>
      {stage !== "idle" && !acct.env && (
        <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]" data-testid="account-email-form">
          {stage === "edit" ? (
            <>
              <input data-testid="account-new-email" type="email" className={fld} placeholder="new@email.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              <button data-testid="account-send-code-btn" onClick={request} disabled={busy || !newEmail.includes("@")} className="rounded-full bg-sbg-burgundy px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{busy ? "Sending…" : "Send code"}</button>
            </>
          ) : (
            <>
              <input data-testid="account-email-code" inputMode="numeric" className={fld} placeholder="6-digit code sent to your new email" value={code} onChange={(e) => setCode(e.target.value)} />
              <button data-testid="account-confirm-email-btn" onClick={confirm} disabled={busy || code.length < 6} className="rounded-full bg-sbg-fuchsia px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{busy ? "Checking…" : "Confirm"}</button>
            </>
          )}
          <button onClick={() => { setStage("idle"); setCode(""); }} className="text-left text-xs font-semibold text-sbg-burgundy/60 hover:underline sm:col-span-2">Cancel</button>
        </div>
      )}
    </div>
  );
}
